import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Listing, FirstPartyReviewsResponse } from '../types';
import { fetchListingById, fetchFirstPartyReviews, trackEvent } from '../services/api';
import { getListingPhoto, getRatingLabel, getRatingDisplay, listingImageUrl, stateNameToSlug, stateAbbreviations } from '../services/listingHelpers';
import StarRating from '../components/StarRating';
import AdUnit from '../components/AdUnit';
import ContactFormModal from '../components/ContactFormModal';
import ReviewForm from '../components/ReviewForm';
import EditorialInquiryForm from '../components/EditorialInquiryForm';
import RatingProvenance from '../components/RatingProvenance';
import { useIsMobile } from '../hooks/useIsMobile';
import { trackAnalyticsEvent } from '../services/analytics';

const ListingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [firstPartyReviews, setFirstPartyReviews] = useState<FirstPartyReviewsResponse | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'reviews' | 'staff' | 'photos'>('overview');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [reviewFormOpen, setReviewFormOpen] = useState(false);
  const [inquiryFormOpen, setInquiryFormOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!id) return;
    let timer: number | undefined;
    fetchListingById(Number(id))
      .then(l => {
        setListing(l);
        setLoading(false);
        timer = window.setTimeout(() => {
          if (document.visibilityState === 'visible') {
            trackEvent({ listingId: Number(id), eventType: 'listing_view', source: 'organic_spa' }).catch(() => {});
          }
        }, 1500);
      })
      .catch(() => setLoading(false));
    fetchFirstPartyReviews(Number(id))
      .then(setFirstPartyReviews)
      .catch(() => {});
    return () => { if (timer !== undefined) window.clearTimeout(timer); };
  }, [id]);

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>Loading...</div>;
  if (!listing) return (
    <div style={{ padding: '60px', textAlign: 'center' }}>
      <h2>Listing not found</h2>
      <Link to="/search">← Back to search</Link>
    </div>
  );

  const photo = getListingPhoto(listing);
  const ratingDisplay = getRatingDisplay(listing);
  // First-party aggregate may arrive from the live endpoint before the
  // nightly sync lands on the listing document — prefer the live value.
  const liveFp = firstPartyReviews?.aggregate;
  const fpCount = Math.max(liveFp?.count || 0, ratingDisplay.firstPartyCount);
  const fpValue = (liveFp?.count || 0) >= ratingDisplay.firstPartyCount
    ? (liveFp?.value || 0)
    : ratingDisplay.firstPartyValue;
  const headlineIsFirstParty = fpCount >= 5 || ratingDisplay.headlineSource === 'first-party';
  const rating = headlineIsFirstParty && fpCount > 0 ? fpValue : ratingDisplay.headlineValue;
  const stateSlug = listing.address?.region
    ? stateNameToSlug(stateAbbreviations[listing.address.region] || listing.address.region)
    : 'florida';

  const primaryPhone = listing.phones && listing.phones.length > 0 ? listing.phones[0] : null;
  const facilityWebsite = listing.vipSubscriptions?.find(v => v.status === 'active' && v.website)?.website;

  const sectionStyle: React.CSSProperties = {
    background: '#fff', borderRadius: '12px', padding: '28px',
    marginBottom: '24px', border: '1px solid #e0e0e0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  };

  const tagStyle: React.CSSProperties = {
    display: 'inline-block', padding: '4px 12px',
    borderRadius: '20px', fontSize: '13px', margin: '2px',
    background: '#e3f2fd', color: '#1565c0',
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 20px' }}>
      <nav style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
        <Link to="/" style={{ color: '#1a237e', textDecoration: 'none' }}>Home</Link>
        {' / '}
        <Link to={`/search?state=${listing.address?.region || ''}`} style={{ color: '#1a237e', textDecoration: 'none' }}>
          {stateAbbreviations[listing.address?.region || ''] || listing.address?.region}
        </Link>
        {' / '}
        {listing.name}
      </nav>

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Hero */}
          <div style={{
            ...sectionStyle,
            backgroundImage: photo
              ? `linear-gradient(rgba(26,35,126,0.7), rgba(26,35,126,0.7)), url(${photo})`
              : 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
            backgroundSize: 'cover', backgroundPosition: 'center',
            color: '#fff',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <h1 style={{ margin: '0 0 8px', fontSize: '32px', fontWeight: 800 }}>{listing.name}</h1>
                {listing.organizationName && listing.organizationName !== listing.name && (
                  <p style={{ margin: '0 0 8px', opacity: 0.85 }}>{listing.organizationName}</p>
                )}
                <p style={{ margin: '0 0 12px', fontSize: '16px', opacity: 0.9 }}>
                  📍 {listing.address?.streetAddress ? `${listing.address.streetAddress}, ` : ''}{listing.address?.city}, {listing.address?.region}
                </p>
                {rating > 0 && (
                  <>
                    <StarRating rating={rating} size="lg" />
                    <span style={{ marginLeft: '12px', opacity: 0.85 }}>
                      {headlineIsFirstParty && fpCount > 0
                        ? `${rating.toFixed(1)} · ${fpCount} verified TopRatedRehabs reviews`
                        : `Historical rating: ${rating.toFixed(1)} (${ratingDisplay.historicalCount} reviews via Recovery Brands)`}
                      {getRatingLabel(rating) ? ` · ${getRatingLabel(rating)}` : ''}
                    </span>
                  </>
                )}
                {!headlineIsFirstParty && fpCount > 0 && (
                  <div style={{ fontSize: '14px', opacity: 0.9, marginTop: '4px' }}>
                    ✓ {fpCount} verified TopRatedRehabs review{fpCount === 1 ? '' : 's'}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {listing.vipSubscriptions?.some(v => v.status === 'active') && (
                  <span style={{ background: '#f5a623', padding: '6px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: 700, textAlign: 'center' }}>⭐ VIP Listing</span>
                )}
                {listing.claimed && (
                  <span style={{ background: '#43a047', padding: '6px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: 700, textAlign: 'center' }}>✓ Claimed</span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexWrap: 'wrap' }}>
              {primaryPhone && (
                <a href={`tel:${primaryPhone.telLink || primaryPhone.display}`}
                  onClick={() => { trackEvent({ listingId: listing.listingId, eventType: 'phone_click', source: 'organic_spa' }).catch(() => {}); trackAnalyticsEvent('phone_click', { placement: 'listing_hero' }); }} style={{
                  padding: '14px 28px', background: '#43a047', color: '#fff',
                  borderRadius: '8px', textDecoration: 'none', fontWeight: 700, fontSize: '16px',
                }}>📞 {primaryPhone.display}</a>
              )}
              {facilityWebsite && /^https?:\/\//i.test(facilityWebsite) && (
                <a href={facilityWebsite} target="_blank" rel="noopener noreferrer sponsored"
                  onClick={() => { trackEvent({ listingId: listing.listingId, eventType: 'website_click', source: 'organic_spa' }).catch(() => {}); trackAnalyticsEvent('website_click', { placement: 'listing_hero' }); }} style={{
                  padding: '14px 28px', background: '#00695c', color: '#fff',
                  borderRadius: '8px', textDecoration: 'none', fontWeight: 700, fontSize: '16px',
                }}>Visit Facility Website</a>
              )}
              <button onClick={() => { trackEvent({ listingId: listing.listingId, eventType: 'help_click', source: 'organic_spa' }).catch(() => {}); trackAnalyticsEvent('contact_start', { placement: 'help_cta' }); setContactModalOpen(true); }} style={{
                padding: '14px 28px', background: '#f5a623', color: '#fff',
                border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '16px', cursor: 'pointer',
              }}>Get Help Now</button>
              <button onClick={() => { trackEvent({ listingId: listing.listingId, eventType: 'contact_start', source: 'organic_spa' }).catch(() => {}); setContactModalOpen(true); }} style={{
                padding: '14px 28px', background: '#1a237e', color: '#fff',
                border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '16px', cursor: 'pointer',
              }}>✉️ Contact This Facility</button>
              {!listing.claimed && (
                <Link to={`/claim/${listing.listingId}`}
                  onClick={() => trackAnalyticsEvent('claim_start', { placement: 'listing_hero' })} style={{
                  padding: '14px 28px', background: 'rgba(255,255,255,0.2)', color: '#fff',
                  borderRadius: '8px', textDecoration: 'none', fontWeight: 700, fontSize: '16px',
                  border: '1px solid rgba(255,255,255,0.4)',
                }}>Claim This Listing</Link>
              )}
            </div>
          </div>

          {/* Owner CTA — shown for unclaimed listings */}
          {!listing.claimed && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
              padding: '14px 20px', marginBottom: '20px',
              background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px',
            }}>
              <div>
                <strong style={{ fontSize: '14px', color: '#5d4037' }}>Is this your facility?</strong>
                <span style={{ fontSize: '13px', color: '#795548', marginLeft: '8px' }}>Claim this listing to manage your profile, respond to reviews, and advertise.</span>
              </div>
              <Link to={`/claim/${listing.listingId}`}
                onClick={() => trackAnalyticsEvent('claim_start', { placement: 'owner_cta' })} style={{
                padding: '8px 18px', background: '#1a237e', color: '#fff',
                borderRadius: '6px', textDecoration: 'none', fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap',
              }}>Claim This Listing →</Link>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '2px solid #e0e0e0', overflowX: 'auto' }}>
            {(['overview', 'reviews', 'staff', 'photos'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{
                padding: '12px 24px', background: 'none', border: 'none',
                borderBottom: activeTab === tab ? '3px solid #1a237e' : '3px solid transparent',
                color: activeTab === tab ? '#1a237e' : '#666',
                fontWeight: activeTab === tab ? 700 : 400,
                cursor: 'pointer', fontSize: '15px', textTransform: 'capitalize',
              }}>{tab}</button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              <div style={sectionStyle}>
                <h2 style={{ margin: '0 0 20px', fontSize: '22px' }}>Services &amp; Treatment</h2>
                {listing.listingType && listing.listingType.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong style={{ fontSize: '14px', color: '#555' }}>Listing Type: </strong>
                    {listing.listingType.map((t, i) => <span key={i} style={tagStyle}>{t}</span>)}
                  </div>
                )}
                {listing.treatmentTypes && listing.treatmentTypes.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong style={{ fontSize: '14px', color: '#555', display: 'block', marginBottom: '8px' }}>Treatment Types</strong>
                    {listing.treatmentTypes.map((t, i) => <span key={i} style={tagStyle}>{t}</span>)}
                  </div>
                )}
                {listing.programTypes && listing.programTypes.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong style={{ fontSize: '14px', color: '#555', display: 'block', marginBottom: '8px' }}>Program Types</strong>
                    {listing.programTypes.map((t, i) => <span key={i} style={{ ...tagStyle, background: '#f3e5f5', color: '#6a1b9a' }}>{t}</span>)}
                  </div>
                )}
                {listing.services && listing.services.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong style={{ fontSize: '14px', color: '#555', display: 'block', marginBottom: '8px' }}>Services</strong>
                    {listing.services.map((s, i) => <span key={i} style={{ ...tagStyle, background: '#e8f5e9', color: '#2e7d32' }}>{s}</span>)}
                  </div>
                )}
                {listing.environmentalSetting && listing.environmentalSetting.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong style={{ fontSize: '14px', color: '#555', display: 'block', marginBottom: '8px' }}>Setting</strong>
                    {listing.environmentalSetting.map((s, i) => <span key={i} style={{ ...tagStyle, background: '#fff3e0', color: '#e65100' }}>{s}</span>)}
                  </div>
                )}
                {listing.genderRestrictions && listing.genderRestrictions.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong style={{ fontSize: '14px', color: '#555', display: 'block', marginBottom: '8px' }}>Gender</strong>
                    {listing.genderRestrictions.map((g, i) => <span key={i} style={tagStyle}>{g}</span>)}
                  </div>
                )}
              </div>

              <div style={sectionStyle}>
                <h2 style={{ margin: '0 0 20px', fontSize: '22px' }}>Insurance &amp; Financing</h2>
                {listing.insuranceAccepted && listing.insuranceAccepted.length > 0 ? (
                  <div style={{ marginBottom: '16px' }}>
                    <strong style={{ fontSize: '14px', color: '#555', display: 'block', marginBottom: '8px' }}>Insurance Accepted</strong>
                    {listing.insuranceAccepted.map((ins, i) => (
                      <span key={i} style={{ ...tagStyle, background: '#e8f5e9', color: '#2e7d32' }}>✓ {ins}</span>
                    ))}
                  </div>
                ) : <p style={{ color: '#888' }}>Contact facility for insurance information</p>}
                {listing.financingAvailable && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: '#fff8e1', borderRadius: '8px', marginTop: '12px' }}>
                    <span style={{ fontSize: '20px' }}>💳</span>
                    <span style={{ fontWeight: 600 }}>Financing Available</span>
                  </div>
                )}
              </div>

              {listing.highlights && listing.highlights.length > 0 && (
                <div style={sectionStyle}>
                  <h2 style={{ margin: '0 0 20px', fontSize: '22px' }}>Highlights</h2>
                  <ul style={{ margin: 0, padding: '0 0 0 20px', lineHeight: 2 }}>
                    {listing.highlights.map((h, i) => (
                      <li key={i} style={{ color: '#333', fontSize: '15px' }}>
                        <strong>{h.title}</strong>
                        {h.description ? ` — ${h.description}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {listing.specializations && listing.specializations.length > 0 && (
                <div style={sectionStyle}>
                  <h2 style={{ margin: '0 0 20px', fontSize: '22px' }}>Specializations</h2>
                  <ul style={{ margin: 0, padding: '0 0 0 20px', lineHeight: 2 }}>
                    {listing.specializations.map((s, i) => (
                      <li key={i} style={{ color: '#333', fontSize: '15px' }}>
                        <strong>{s.title}</strong>
                        {s.description ? ` — ${s.description}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {listing.editorialReview && listing.editorialReview.length > 0 && (
                <div style={sectionStyle}>
                  <h2 style={{ margin: '0 0 20px', fontSize: '22px' }}>Editorial Review</h2>
                  {listing.editorialReview
                    .filter(e => e.editorial && (!('status' in e) || (e as any).status === 'published' || !(e as any).status))
                    .map((e, i) => (
                      <div key={i} style={{ marginBottom: '20px' }}>
                        <p style={{ lineHeight: 1.8, color: '#333', whiteSpace: 'pre-line' }}>{e.editorial}</p>
                        {e.author && <p style={{ color: '#888', fontSize: '13px', marginTop: '8px' }}>— {e.author}</p>}
                        {e.publishedAt && (
                          <p style={{ color: '#aaa', fontSize: '12px', margin: '4px 0 0' }}>
                            Published: {e.publishedAt}
                          </p>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {(!listing.editorialReview ||
                listing.editorialReview.filter(e => e.editorial && (!('status' in e) || (e as any).status === 'published' || !(e as any).status)).length === 0) && (
                <div style={{ ...sectionStyle, background: 'linear-gradient(135deg,#f5f7ff 0%,#fff 100%)' }}>
                  <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>Want to write an editorial?</h3>
                  <p style={{ margin: '0 0 16px', color: '#555', fontSize: 14 }}>
                    Are you a writer, clinician, or industry expert? Pitch the listing owner — if they accept, you'll get a one-time link to publish your editorial here.
                  </p>
                  <button
                    type="button"
                    onClick={() => setInquiryFormOpen(true)}
                    style={{ padding: '10px 18px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                  >
                    Inquire to write an editorial
                  </button>
                </div>
              )}

              {listing.ratings && ratingDisplay.historicalCount > 0 && (
                <div style={sectionStyle}>
                  <h2 style={{ margin: '0 0 20px', fontSize: '22px' }}>Ratings</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: '16px' }}>
                    {[
                      { label: 'Overall', value: listing.ratings.overall?.value },
                      { label: 'Accommodations', value: listing.ratings.accommodations?.value },
                      { label: 'Treatment Effectiveness', value: listing.ratings.treatmentEffectiveness?.value },
                      { label: 'Meals & Nutrition', value: listing.ratings.mealsAndNutrition?.value },
                    ].filter(r => r.value && r.value > 0).map(r => (
                      <div key={r.label} style={{ textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '32px', fontWeight: 800, color: '#1a237e' }}>{r.value!.toFixed(1)}</div>
                        <StarRating rating={r.value!} size="sm" />
                        <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>{r.label}</div>
                      </div>
                    ))}
                  </div>
                  {listing.reviewSummary && (
                    <div style={{ marginTop: '16px', padding: '16px', background: '#f5f7ff', borderRadius: '8px' }}>
                      <h3 style={{ margin: '0 0 8px', fontSize: '15px' }}>What reviewers said</h3>
                      <p style={{ margin: '0 0 8px', lineHeight: 1.7, color: '#333', fontSize: '14px' }}>{listing.reviewSummary}</p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>
                        AI-generated summary of {ratingDisplay.historicalCount} reviews collected on
                        rehabs.com and recovery.org through 2023. Not a review or medical advice.
                      </p>
                    </div>
                  )}
                  <div style={{ marginTop: '16px' }}>
                    <RatingProvenance count={ratingDisplay.historicalCount} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Reviews Tab — first-party verified reviews only; licensed
              historical review text is not republished (see review policy). */}
          {activeTab === 'reviews' && (
            <div style={sectionStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                <h2 style={{ margin: 0, fontSize: '22px' }}>
                  Verified Reviews ({firstPartyReviews?.count || 0})
                </h2>
                <button
                  type="button"
                  onClick={() => setReviewFormOpen(true)}
                  style={{ padding: '10px 18px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                >
                  Write a review
                </button>
              </div>

              {firstPartyReviews && firstPartyReviews.reviews.length > 0 ? (
                firstPartyReviews.reviews.map((r) => (
                  <div key={r.publicId} style={{ padding: '20px 0', borderBottom: '1px solid #eee' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span>
                        <strong>{r.author}</strong>
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#2e7d32', fontWeight: 600 }}>✓ Verified reviewer</span>
                        {r.approvedAt && <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>{r.approvedAt}</span>}
                      </span>
                      {(r.ratings.treatmentEffectiveness || r.ratings.accommodationsAmenities || r.ratings.mealsNutrition) ? (
                        <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#666' }}>
                          {r.ratings.treatmentEffectiveness ? <span>Treatment <strong style={{ color: '#1a237e' }}>{r.ratings.treatmentEffectiveness}/5</strong></span> : null}
                          {r.ratings.accommodationsAmenities ? <span>Accommodations <strong style={{ color: '#1a237e' }}>{r.ratings.accommodationsAmenities}/5</strong></span> : null}
                          {r.ratings.mealsNutrition ? <span>Meals <strong style={{ color: '#1a237e' }}>{r.ratings.mealsNutrition}/5</strong></span> : null}
                        </div>
                      ) : null}
                    </div>
                    <p style={{ margin: 0, color: '#333', lineHeight: 1.6 }}>{r.text}</p>
                    {r.officialResponse && (
                      <div style={{ marginTop: 14, padding: '14px 16px', background: '#f5f7ff', borderLeft: '4px solid #1a237e', borderRadius: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                          <strong style={{ color: '#1a237e' }}>Response from {r.officialResponse.author}</strong>
                          {r.officialResponse.createdAt && (
                            <span style={{ color: '#888', fontSize: 12 }}>{r.officialResponse.createdAt}</span>
                          )}
                        </div>
                        <p style={{ margin: 0, color: '#333', lineHeight: 1.6 }}>{r.officialResponse.text}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p style={{ color: '#888' }}>
                  No verified TopRatedRehabs reviews yet for this facility — be the first to share your experience.
                </p>
              )}

              {ratingDisplay.historicalCount > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <h3 style={{ margin: '0 0 8px', fontSize: '16px' }}>Historical rating</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <StarRating rating={ratingDisplay.historicalValue} size="sm" />
                    <span style={{ fontSize: 14, color: '#555' }}>
                      {ratingDisplay.historicalValue.toFixed(1)} · {ratingDisplay.historicalCount} reviews
                    </span>
                  </div>
                  {listing.reviewSummary && (
                    <div style={{ padding: '14px 16px', background: '#f5f7ff', borderRadius: 8, marginBottom: 12 }}>
                      <p style={{ margin: '0 0 8px', lineHeight: 1.7, color: '#333', fontSize: 14 }}>{listing.reviewSummary}</p>
                      <p style={{ margin: 0, fontSize: 12, color: '#888' }}>
                        AI-generated summary of {ratingDisplay.historicalCount} reviews collected on
                        rehabs.com and recovery.org through 2023. Not a review or medical advice.
                      </p>
                    </div>
                  )}
                  <RatingProvenance count={ratingDisplay.historicalCount} />
                </div>
              )}
            </div>
          )}

          {/* Staff Tab */}
          {activeTab === 'staff' && (
            <div style={sectionStyle}>
              <h2 style={{ margin: '0 0 20px', fontSize: '22px' }}>Staff</h2>
              {listing.staff && listing.staff.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))', gap: '20px' }}>
                  {listing.staff.map((s, i) => (
                    <div key={i} style={{ textAlign: 'center', padding: '20px', border: '1px solid #eee', borderRadius: '8px' }}>
                      {listingImageUrl(listing, 400, s.fileName) ? (
                        <img
                          src={listingImageUrl(listing, 400, s.fileName)!}
                          alt={s.name}
                          style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', marginBottom: '12px' }}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#e0e0e0', margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>👤</div>
                      )}
                      <h4 style={{ margin: '0 0 4px' }}>{s.name}</h4>
                      {s.title && <p style={{ margin: '0 0 8px', color: '#666', fontSize: '13px' }}>{s.title}</p>}
                      {s.bio && <p style={{ margin: 0, fontSize: '13px', color: '#555', lineHeight: 1.5 }}>{s.bio}</p>}
                    </div>
                  ))}
                </div>
              ) : <p style={{ color: '#888' }}>No staff information available.</p>}
            </div>
          )}

          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <div style={sectionStyle}>
              <h2 style={{ margin: '0 0 20px', fontSize: '22px' }}>Photos</h2>
              {listing.photos && listing.photos.some(p => listingImageUrl(listing, 800, p.fileName)) ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: '12px' }}>
                  {listing.photos.filter(p => listingImageUrl(listing, 800, p.fileName)).map((p, i) => (
                    <div key={i}>
                      <img
                        src={listingImageUrl(listing, 800, p.fileName)!}
                        alt={p.altText || p.caption || listing.name}
                        style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                      {p.caption && <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{p.caption}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📷</div>
                  <p>No photos available for this listing.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar — hidden on mobile since CTA buttons are in hero */}
        <aside style={{ width: '300px', flexShrink: 0, display: isMobile ? 'none' : 'block' }}>
          <div style={{ ...sectionStyle, position: 'sticky', top: '80px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>Contact This Facility</h3>
            {primaryPhone && (
              <a href={`tel:${primaryPhone.telLink || primaryPhone.display}`}
                onClick={() => { trackEvent({ listingId: listing.listingId, eventType: 'phone_click', source: 'organic_spa' }).catch(() => {}); trackAnalyticsEvent('phone_click', { placement: 'listing_sidebar' }); }} style={{
                display: 'block', width: '100%', padding: '14px',
                background: '#43a047', color: '#fff', textAlign: 'center',
                borderRadius: '8px', textDecoration: 'none', fontWeight: 700, fontSize: '16px',
                marginBottom: '12px',
              }}>📞 {primaryPhone.display}</a>
            )}
            <button onClick={() => { trackEvent({ listingId: listing.listingId, eventType: 'help_click', source: 'organic_spa' }).catch(() => {}); trackAnalyticsEvent('contact_start', { placement: 'help_cta_sidebar' }); setContactModalOpen(true); }} style={{
              width: '100%', padding: '14px', background: '#1a237e', color: '#fff',
              border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '16px',
              cursor: 'pointer', marginBottom: '16px',
            }}>Get Help Now</button>

            {listing.address && (
              <div style={{ fontSize: '14px', color: '#555', lineHeight: 1.8 }}>
                <strong>Address</strong><br />
                {listing.address.streetAddress && <>{listing.address.streetAddress}<br /></>}
                {listing.address.city}, {listing.address.region} {listing.address.zipCode}
              </div>
            )}

            {listing.phones && listing.phones.length > 1 && (
              <div style={{ marginTop: '16px', fontSize: '14px' }}>
                <strong>Additional Numbers</strong>
                {listing.phones.slice(1).map((p, i) => (
                  <div key={i} style={{ marginTop: '4px' }}>
                    <a href={`tel:${p.telLink || p.display}`}
                      onClick={() => { trackEvent({ listingId: listing.listingId, eventType: 'phone_click', source: 'organic_spa' }).catch(() => {}); trackAnalyticsEvent('phone_click', { placement: 'listing_additional_phone' }); }}
                      style={{ color: '#1a237e', textDecoration: 'none' }}>
                      {p.display}
                    </a>
                    {p.description && <span style={{ color: '#888', marginLeft: '4px' }}>({p.description})</span>}
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: '20px' }}>
              <AdUnit format="sidebar" count={1} stateSlug={stateSlug} />
            </div>
          </div>
        </aside>
      </div>

      <ContactFormModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        listingId={listing.listingId}
        listingName={listing.name}
      />

      {reviewFormOpen && (
        <ReviewForm
          listingId={listing.listingId}
          onClose={() => setReviewFormOpen(false)}
        />
      )}

      {inquiryFormOpen && (
        <EditorialInquiryForm
          listingId={listing.listingId}
          listingName={listing.name}
          onClose={() => setInquiryFormOpen(false)}
        />
      )}
    </div>
  );
};

export default ListingDetailPage;
